import { getUserByManager, getUserFilters } from "api/user";
import Select from "react-select";
import { useEffect, useMemo, useState } from "react";
import { useSharedState } from "state";
import { FilterValues } from "types/ui/filter";
import { User, UserFilters } from "types/user";

interface SidebarProps {
  filtersList: FilterValues;
  setFiltersList: React.Dispatch<React.SetStateAction<FilterValues>>;
  onFiltersReset: () => void;
  onFiltersSubmit: () => void;
}

export default function Filters({ filtersList, setFiltersList, onFiltersReset, onFiltersSubmit }: SidebarProps) {
  const {
    user,
    getUserData,
    users,
    getUsersData,
    contractors,
    getContractorsData,
    templates,
    getTemplatesData,
    companies,
    getCompaniesData,
    departments,
    getDepartmentsData,
    levels,
    getLevelsData,
    isAdmin,
    filterQuery,
    notify,
  } = useSharedState();

  const [isFiltersOpened, setFiltersOpened] = useState<boolean>(false);
  const [myUsers, setMyUsers] = useState<User[]>([]);
  const [userFilters, setUserFilters] = useState<UserFilters | undefined>(undefined);

  useEffect(() => {
    const handleBackdropClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.classList.contains("offcanvas-backdrop")) {
        setFiltersOpened(false);
      }
    };

    document.addEventListener("click", handleBackdropClick);
    return () => document.removeEventListener("click", handleBackdropClick);
  }, []);

  const isTaskPage = useMemo((): boolean => {
    return window.location.pathname === "/tasks";
  }, [window.location.pathname]);

  const isDocumentPage = useMemo((): boolean => {
    return window.location.pathname.includes("/documents");
  }, [window.location.pathname]);

  const isPaymentPage = useMemo((): boolean => {
    return window.location.pathname.includes("/payments");
  }, [window.location.pathname]);

  const isBudgetPage = useMemo((): boolean => {
    return window.location.pathname.includes("/budgets");
  }, [window.location.pathname]);

  const isSummaryPage = useMemo((): boolean => {
    return window.location.pathname === "/summary";
  }, [window.location.pathname]);

  const isUserPage = useMemo((): boolean => {
    return window.location.pathname === "/users";
  }, [window.location.pathname]);

  const usersOptions = useMemo(() => {
    return [...users.map((user) => ({ label: user.name, value: user.name, disabled: false }))];
  }, [users]);

  const responsibleOptions = useMemo(() => {
    return [...myUsers.map((user) => ({ label: user.name, value: user.name, disabled: false }))];
  }, [myUsers]);

  const templateOptions = useMemo(() => {
    return [...templates.map((template) => ({ label: template.name, value: template.name, disabled: false }))];
  }, [templates]);

  const contractorOptions = useMemo(() => {
    return [
      ...contractors.map((contractor) => ({
        label: contractor.contragent,
        value: contractor.contragent_id,
        disabled: false,
      })),
    ];
  }, [contractors]);

  const locationOptions = useMemo(() => {
    return [
      ...(isUserPage && userFilters ? userFilters.locations : user.locations).map((location) => ({
        label: location.location,
        value: isUserPage ? location.location_id : location.location,
        disabled: false,
      })),
    ];
  }, [isUserPage, userFilters, user.locations]);

  const statusOptions = useMemo(() => {
    return [
      { label: "К выполнению", value: 0, disabled: false },
      { label: "В работе", value: 1, disabled: false },
      { label: "Завершён", value: 2, disabled: false },
      { label: "Отклонён", value: 3, disabled: false },
      { label: isPaymentPage ? "К закрытию" : "На подписании", value: 4, disabled: false },
    ];
  }, [isPaymentPage]);

  const companyOptions = useMemo(() => {
    return [...companies.map((company) => ({ label: company.company, value: company.company_id, disabled: false }))];
  }, [companies]);

  const departmentOptions = useMemo(() => {
    return [
      ...(isUserPage && userFilters ? userFilters.departments : departments).map((department) => ({
        label: department.department,
        value: department.department_id,
        disabled: false,
      })),
    ];
  }, [isUserPage, userFilters, departments]);

  const budgetOptions = useMemo(() => {
    return [
      ...levels
        .filter((lvl) => lvl.parent_budget_type_id === null)
        .map((lvl) => ({
          label: lvl.budget_type!,
          value: lvl.budget_type_id!,
          disabled: false,
        })),
    ];
  }, [levels]);

  const roleOptions = useMemo(() => {
    return [...(userFilters?.roles.map((role) => ({ label: role.role, value: role.role, disabled: false })) || [])];
  }, [userFilters]);

  useEffect(() => {
    isFiltersOpened && initFilters();
  }, [isFiltersOpened, isTaskPage, isDocumentPage, isPaymentPage]);

  const initFilters = async () => {
    isAdmin && !isUserPage && getUsersData();

    if (isTaskPage) {
      getUserByManagerData();
    } else if (isDocumentPage || isPaymentPage) {
      getTemplatesData();
      getContractorsData();
      getUserData();
    } else if (isBudgetPage) {
      getCompaniesData();
      getDepartmentsData();
      getLevelsData();
    } else if (isSummaryPage) {
      getCompaniesData();
      getDepartmentsData();
    } else if (isUserPage) {
      getUserFiltersData();
    }
  };

  const getUserByManagerData = async () => {
    try {
      const res: User[] = await getUserByManager();
      setMyUsers(res);
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при получении пользователей");
    }
  };

  const getUserFiltersData = async () => {
    try {
      const res: UserFilters = await getUserFilters();
      setUserFilters(res);
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при получении фильтров");
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFiltersList((prev) => ({ ...prev, [name]: value }));
  };

  const appliedFiltersCount = useMemo(() => {
    if (!filterQuery) return 0;
    return Object.values(filterQuery).filter((value) => value !== "" && value != null && value !== undefined).length;
  }, [filterQuery]);

  return (
    <>
      <a
        className="btn btn-ghost-primary grow position-relative"
        data-bs-toggle="offcanvas"
        href="#filters"
        role="button"
        aria-controls="filters"
        onClick={() => setFiltersOpened(true)}
      >
        Фильтры
        {appliedFiltersCount > 0 && (
          <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
            {appliedFiltersCount}
          </span>
        )}
      </a>

      <div className="offcanvas offcanvas-end" tabIndex={-1} id="filters" aria-labelledby="filtersLabel">
        <div className="offcanvas-header">
          <h2 className="offcanvas-title" id="filtersLabel">
            Фильтры
          </h2>
          <button
            type="button"
            className="btn-close text-reset"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
            onClick={() => setFiltersOpened(false)}
          ></button>
        </div>

        <div className="offcanvas-body">
          <div className="d-flex flex-column gap-3">
            {!isUserPage && !isSummaryPage && (
              <div className="d-flex align-items-center row">
                <div className="col-5">Дата создания от</div>
                <div className="col">
                  <input
                    className="form-control"
                    type="date"
                    name="from_date"
                    max="9999-12-31"
                    value={filtersList.from_date}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            )}

            {!isUserPage && !isSummaryPage && (
              <div className="d-flex align-items-center row">
                <div className="col-5">Дата создания до</div>
                <div className="col">
                  <input
                    className="form-control"
                    type="date"
                    name="to_date"
                    max="9999-12-31"
                    value={filtersList.to_date}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            )}

            {(isDocumentPage || isPaymentPage) && (
              <div className="d-flex align-items-center row">
                <div className="col-4">Контрагент</div>
                <div className="col">
                  <Select
                    classNamePrefix="react-select"
                    name="contragent_id"
                    placeholder={"Выберите контрагента"}
                    value={
                      contractorOptions.find((option) => String(option.value) === filtersList.contragent_id) || null
                    }
                    onChange={(option) =>
                      setFiltersList((prev) => ({ ...prev, contragent_id: String(option?.value) || "" }))
                    }
                    options={contractorOptions}
                    isOptionDisabled={(option) => option.disabled}
                    isSearchable
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                  />
                </div>
              </div>
            )}

            {(isDocumentPage || isPaymentPage) && (
              <div className="d-flex align-items-center row">
                <div className="col-4">Шаблон</div>
                <div className="col">
                  <Select
                    classNamePrefix="react-select"
                    name="template_name"
                    placeholder={"Выберите шаблон"}
                    value={templateOptions.find((option) => option.value === filtersList.template_name) || null}
                    onChange={(option) => setFiltersList((prev) => ({ ...prev, template_name: option?.value || "" }))}
                    options={templateOptions}
                    isOptionDisabled={(option) => option.disabled}
                    isSearchable
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                  />
                </div>
              </div>
            )}

            {(isTaskPage || isPaymentPage) && (
              <div className="d-flex align-items-center row">
                <div className="col-4">Проект</div>
                <div className="col">
                  <input
                    className="form-control"
                    type="text"
                    name="project"
                    value={filtersList.project}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            )}

            {isAdmin && !isUserPage && !isSummaryPage && (
              <div className="d-flex align-items-center row">
                <div className="col-4">Создатель</div>
                <div className="col">
                  <Select
                    classNamePrefix="react-select"
                    name="creator_name"
                    placeholder={"Выберите создателя"}
                    value={usersOptions.find((option) => option.value === filtersList.creator_name) || null}
                    onChange={(option) => setFiltersList((prev) => ({ ...prev, creator_name: option?.value || "" }))}
                    options={usersOptions}
                    isOptionDisabled={(option) => option.disabled}
                    isSearchable
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                  />
                </div>
              </div>
            )}

            {isTaskPage && (
              <div className="d-flex align-items-center row">
                <div className="col-4">Ответственный</div>
                <div className="col">
                  <Select
                    classNamePrefix="react-select"
                    name={isTaskPage ? "assigned_to_name" : "approver_name"}
                    placeholder={"Выберите ответственного"}
                    value={
                      responsibleOptions.find(
                        (option) =>
                          option.value === (isTaskPage ? filtersList.assigned_to_name : filtersList.approver_name)
                      ) || null
                    }
                    onChange={(option) =>
                      setFiltersList((prev) => ({
                        ...prev,
                        [isTaskPage ? "assigned_to_name" : "approver_name"]: option?.value || "",
                      }))
                    }
                    options={responsibleOptions}
                    isOptionDisabled={(option) => option.disabled}
                    isSearchable
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                  />
                </div>
              </div>
            )}

            {/* {(isDocumentPage || isPaymentPage || isUserPage) && ( */}
            {isUserPage && (
              <div className="d-flex align-items-center row">
                <div className="col-4">Филиал</div>
                <div className="col">
                  <Select
                    classNamePrefix="react-select"
                    name={isUserPage ? "location_id" : "locations"}
                    placeholder={"Выберите филиал"}
                    value={
                      locationOptions.find(
                        (option) => option.value === (isUserPage ? filtersList.location_id : filtersList.locations)
                      ) || null
                    }
                    onChange={(option) =>
                      setFiltersList((prev) => ({
                        ...prev,
                        [isUserPage ? "location_id" : "locations"]: option?.value || "",
                      }))
                    }
                    options={locationOptions}
                    isOptionDisabled={(option) => option.disabled}
                    isSearchable
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                  />
                </div>
              </div>
            )}

            {(isDocumentPage || isPaymentPage) && (
              <div className="d-flex align-items-center row">
                <div className="col-4">Статус</div>
                <div className="col">
                  <Select
                    classNamePrefix="react-select"
                    name={"status"}
                    placeholder={"Выберите статус"}
                    value={statusOptions.find((option) => option.value === filtersList.status) || null}
                    onChange={(option) =>
                      setFiltersList((prev) => ({
                        ...prev,
                        status: typeof option?.value === "number" ? option.value : undefined,
                      }))
                    }
                    options={statusOptions}
                    isOptionDisabled={(option) => option.disabled}
                    isSearchable
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                  />
                </div>
              </div>
            )}

            {(isBudgetPage || isSummaryPage) && (
              <div className="d-flex align-items-center row">
                <div className="col-4">Компания</div>
                <div className="col">
                  <Select
                    classNamePrefix="react-select"
                    name={"company_id"}
                    placeholder={"Выберите компанию"}
                    value={companyOptions.find((option) => String(option.value) === filtersList.company_id) || null}
                    onChange={(option) =>
                      setFiltersList((prev) => ({ ...prev, company_id: String(option?.value) || "" }))
                    }
                    options={companyOptions}
                    isOptionDisabled={(option) => option.disabled}
                    isSearchable
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                  />
                </div>
              </div>
            )}

            {(isBudgetPage || isSummaryPage || isUserPage) && (
              <div className="d-flex align-items-center row">
                <div className="col-4">Отдел</div>
                <div className="col">
                  <Select
                    classNamePrefix="react-select"
                    name={"department_id"}
                    placeholder={"Выберите отдел"}
                    value={
                      departmentOptions.find((option) => String(option.value) === filtersList.department_id) || null
                    }
                    onChange={(option) =>
                      setFiltersList((prev) => ({ ...prev, department_id: String(option?.value) || "" }))
                    }
                    options={departmentOptions}
                    isOptionDisabled={(option) => option.disabled}
                    isSearchable
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                  />
                </div>
              </div>
            )}

            {isBudgetPage && (
              <div className="d-flex align-items-center row">
                <div className="col-4">Бюджет</div>
                <div className="col">
                  <Select
                    classNamePrefix="react-select"
                    name={"budget_type_id"}
                    placeholder={"Выберите бюджет"}
                    value={budgetOptions.find((option) => String(option.value) === filtersList.budget_type_id) || null}
                    onChange={(option) =>
                      setFiltersList((prev) => ({ ...prev, budget_type_id: String(option?.value) || "" }))
                    }
                    options={budgetOptions}
                    isOptionDisabled={(option) => option.disabled}
                    isSearchable
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                  />
                </div>
              </div>
            )}

            {(isBudgetPage || isSummaryPage) && (
              <div className="d-flex align-items-center row">
                <div className="col-4">Год</div>
                <div className="col">
                  <input
                    className="form-control"
                    type="text"
                    name="year"
                    value={filtersList.year}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            )}

            {isUserPage && (
              <>
                <div className="d-flex align-items-center row">
                  <div className="col-4">Роль</div>
                  <div className="col">
                    <Select
                      classNamePrefix="react-select"
                      name={"role"}
                      placeholder={"Выберите роль"}
                      value={roleOptions.find((option) => option.value === filtersList.role) || null}
                      onChange={(option) => setFiltersList((prev) => ({ ...prev, role: option?.value || "" }))}
                      options={roleOptions}
                      isOptionDisabled={(option) => option.disabled}
                      isSearchable
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      }}
                    />
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="d-flex align-items-center justify-content-end gap-3 mt-3">
            <button
              className="btn btn-ghost-primary"
              type="button"
              onClick={onFiltersReset}
              data-bs-dismiss="offcanvas"
            >
              Сбросить фильтры
            </button>
            <button className="btn btn-primary" type="button" onClick={onFiltersSubmit} data-bs-dismiss="offcanvas">
              Применить
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
